import React, { useCallback, useMemo, useRef, useState } from 'react';
import { animated, useSpring, useTransition } from 'react-spring';
import { Navbar } from '@ard-online/component-styleguide/dist/components/Navbar/Navbar';
import { Content, Root, Wrapper } from '@ard-online/component-styleguide/dist/components/Tablist/styles';
import { easeInOut, easeOut } from '@ard-online/component-styleguide/dist/components/primitives/Easing';
import { useUIDSeed } from 'react-uid';

type TablistProps = {
  initialTab?: number;
  isCollapsable?: boolean;
  navbarAlignment?: 'left' | 'center' | 'right';
  tabs: Array<{ title: string; content: React.ReactNode }>;
  hasDisabledAnimations?: boolean;
  onTabChange?: (index: number, title: string) => void;
  /** ARDONI7-51253: passes aria-label to the internal Navbar's <nav>. */
  label?: string;
};

function useTabs(tabs: TablistProps['tabs'], uidSeed: (id: string) => string) {
  return useMemo(
    () =>
      tabs.map((tab, index) =>
        ({ style }: { style: React.CSSProperties }) => (
          <animated.div
            id={uidSeed(`tabpanel${index + 1}`)}
            role="tabpanel"
            aria-labelledby={uidSeed(`tab${index + 1}`)}
            style={style}
          >
            {tab.content}
          </animated.div>
        )
      ),
    [tabs, uidSeed]
  );
}

function useContentAnimation(activeTab: number, lastActiveTab: number, hasDisabledAnimations: boolean) {
  return useTransition(activeTab, {
    from: {
      opacity: 0,
      transform: `translate3d(${activeTab > lastActiveTab ? '' : '-'}15%,0,0)`,
      position: 'relative',
    },
    enter: { opacity: 1, transform: 'translate3d(0%,0,0)', position: 'relative' },
    leave: {
      opacity: 0,
      transform: `translate3d(${activeTab < lastActiveTab ? '' : '-'}15%,0,0)`,
      position: 'absolute',
    },
    immediate: hasDisabledAnimations,
    config: { duration: 200, easing: (t: number) => easeInOut(t) },
  });
}

function useCollapseAnimation(isCollapsable: boolean, isCollapsed: boolean, contentRef: React.RefObject<HTMLDivElement>, hasDisabledAnimations: boolean) {
  return useSpring(
    isCollapsable
      ? {
          height: isCollapsed ? 0 : contentRef.current && contentRef.current.clientHeight,
          immediate: hasDisabledAnimations,
          config: { duration: 200, easing: (t: number) => easeOut(t) },
        }
      : {}
  );
}

function useOnTabChangeCallback(onTabChange?: (index: number, title: string) => void) {
  return useCallback(
    (index: number, title: string) => {
      if (onTabChange) onTabChange(index, title);
    },
    [onTabChange]
  );
}

function getButtonProps(
  tabs: TablistProps['tabs'],
  uidSeed: (id: string) => string,
  isCollapsed: boolean,
  activeTabRef: React.MutableRefObject<number>,
  isCollapsable: boolean,
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>,
  setLastActiveTab: React.Dispatch<React.SetStateAction<number>>,
  setActiveTab: React.Dispatch<React.SetStateAction<number>>,
  onTabChangeMemo: (index: number, title: string) => void
) {
  return tabs.map(({ title }, index) => ({
    text: title,
    id: uidSeed(`tab${index + 1}`),
    isActive: !isCollapsed && index === activeTabRef.current,
    ariaControls: uidSeed(`tabpanel${index + 1}`),
    onClick: () => {
      if (index === activeTabRef.current) {
        if (isCollapsable) setIsCollapsed((wasCollapsed) => !wasCollapsed);
      } else {
        setIsCollapsed(false);
        setLastActiveTab(activeTabRef.current);
        setActiveTab(index);
        onTabChangeMemo(index, title);
      }
    },
  }));
}

export function Tablist({
  initialTab = 0,
  isCollapsable = false,
  navbarAlignment = 'center',
  tabs,
  hasDisabledAnimations = false,
  onTabChange,
  label = 'Inhaltsbereiche',
}: TablistProps) {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(isCollapsable);
  const [lastActiveTab, setLastActiveTab] = useState<number>(-1);
  const [activeTab, setActiveTab] = useState<number>(initialTab);
  const contentRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<number>(-1);
  activeTabRef.current = activeTab;
  const uidSeed = useUIDSeed();
  const tabsMemo = useTabs(tabs, uidSeed);
  const contentAnimation = useContentAnimation(activeTab, lastActiveTab, hasDisabledAnimations);
  const collapseAnimation = useCollapseAnimation(isCollapsable, isCollapsed, contentRef, hasDisabledAnimations);
  const onTabChangeMemo = useOnTabChangeCallback(onTabChange);
  const buttonProps = getButtonProps(
    tabs,
    uidSeed,
    isCollapsed,
    activeTabRef,
    isCollapsable,
    setIsCollapsed,
    setLastActiveTab,
    setActiveTab,
    onTabChangeMemo
  );

  return (
    <Root>
      <Navbar
        items={buttonProps as any}
        isSwipeable
        type="register"
        buttonType="tab"
        accessibility="tablist"
        alignment={navbarAlignment}
        label={label}
      />
      <Wrapper style={collapseAnimation}>
        <Content ref={contentRef}>
          {contentAnimation((styles: any, item: number) => {
            const ActiveTab = tabsMemo[item];
            return <ActiveTab style={styles} />;
          })}
        </Content>
      </Wrapper>
    </Root>
  );
}

export default React.memo(Tablist);
